/**
 * Phase A hotfix — Helper unique pour afficher la plage horaire d'un RDV.
 *
 * Utilisé partout où on affiche un RDV pour éviter les divergences de
 * formatage entre pages (fiche projet, dashboard, fiche contact, agenda…).
 *
 * @param {string} startISO   - `rdv.scheduled_at` (ISO string UTC)
 * @param {number} durationMinutes - `rdv.duration_minutes` (int)
 * @returns {string} - Ex : `"14:00 – 15:30"` (24h, tiret cadratin fin).
 *                     Retourne `""` si `startISO` invalide.
 */
/**
 * Convertit une ISO string UTC vers `"YYYY-MM-DDTHH:mm"` en heure locale
 * Europe/Brussels. Gère l'heure d'été/d'hiver automatiquement via `Intl`.
 *
 * Ex : `"2026-09-05T07:00:00+00:00"` → `"2026-09-05T09:00"` (été CEST +2)
 *      `"2026-11-05T08:00:00Z"`      → `"2026-11-05T09:00"` (hiver CET +1)
 */
export function isoUtcToBrusselsLocal(isoUtc) {
  if (!isoUtc) return "";
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Brussels", hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }).formatToParts(d);
  const o = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${o.year}-${o.month}-${o.day}T${o.hour}:${o.minute}`;
}

/**
 * Convertit `"YYYY-MM-DDTHH:mm"` (interprété Europe/Brussels) vers ISO UTC.
 * Utilise un round-trip via `Intl` pour connaître l'offset exact à cette
 * date (gère DST) : jamais de `-2h` hardcodé, jamais de dépendance à la
 * timezone du navigateur.
 *
 * Ex : `"2026-09-05T09:00"` → `"2026-09-05T07:00:00.000Z"` (été)
 *      `"2026-11-05T09:00"` → `"2026-11-05T08:00:00.000Z"` (hiver)
 */
export function brusselsLocalToIsoUtc(local) {
  if (!local || !local.includes("T")) return "";
  const [d, t] = local.split("T");
  const [y, mo, da] = d.split("-").map(Number);
  const [h, mi] = (t || "00:00").split(":").map(Number);
  if ([y, mo, da, h, mi].some((n) => Number.isNaN(n))) return "";
  // 1. On construit une Date "as if UTC" avec l'heure Bruxelles saisie.
  const asIfUtc = new Date(Date.UTC(y, mo - 1, da, h, mi));
  // 2. On regarde à quelle heure Bruxelles cela correspond → révèle l'offset.
  const brusLocal = isoUtcToBrusselsLocal(asIfUtc.toISOString());
  const [bd, bt] = brusLocal.split("T");
  const [by, bmo, bda] = bd.split("-").map(Number);
  const [bh, bmi] = bt.split(":").map(Number);
  const brusselsMs = Date.UTC(by, bmo - 1, bda, bh, bmi);
  const offsetMs = brusselsMs - asIfUtc.getTime();
  // 3. UTC réel = "as if UTC" − offset Bruxelles.
  return new Date(asIfUtc.getTime() - offsetMs).toISOString();
}

export function formatTimeSlot(startISO, durationMinutes) {
  if (!startISO) return "";
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return "";
  const duration = Number(durationMinutes);
  if (!Number.isFinite(duration) || duration <= 0) {
    // Rendu défensif : si la durée est absente/invalide, on affiche juste
    // l'heure de début (jamais un slot faux comme "14:00 – 14:00").
    return formatHM(start);
  }
  const end = new Date(start.getTime() + duration * 60_000);
  return `${formatHM(start)} – ${formatHM(end)}`;
}

/**
 * Formate un `Date` en `HH:MM` 24h Europe/Brussels.
 */
export function formatHM(date) {
  return date.toLocaleTimeString("fr-BE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Brussels",
  });
}

/**
 * Date + plage horaire complète en une string.
 * Ex : `"03/09/2026 · 14:00 – 15:30"`
 */
export function formatDateAndTimeSlot(startISO, durationMinutes) {
  if (!startISO) return "";
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return "";
  const dateStr = start.toLocaleDateString("fr-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Brussels",
  });
  const slot = formatTimeSlot(startISO, durationMinutes);
  return slot ? `${dateStr} · ${slot}` : dateStr;
}
